import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import RequireAuth from "./components/auth/RequireAuth";

// Lazily loaded pages (code-splitting)
const NotFound      = lazy(() => import("./pages/NotFound"));
const Customize     = lazy(() => import("./pages/Customize"));
const Home          = lazy(() => import("./pages/Home"));
const Interview     = lazy(() => import("./pages/Interview"));
const Auth          = lazy(() => import("./pages/Auth"));
const Settings      = lazy(() => import("./pages/Settings"));
const Pricing       = lazy(() => import("./pages/Pricing"));
const Notes         = lazy(() => import("./pages/Notes"));
const UserDetails   = lazy(() => import("./pages/UserDetails"));
const AssistantMode = lazy(() => import("./pages/AssistantMode"));
const Assistant     = lazy(() => import("./pages/Assistant"));
const AssistantChat = lazy(() => import("./pages/AssistantChat"));

// Lazy load layout to reduce initial bundle
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));

const queryClient = new QueryClient();

// Minimal, tasteful route-loading skeleton
function RouteSkeleton() {
  return (
    <div className="w-full h-dvh flex">
      {/* Left gutter roughly where the sidebar sits (keeps layout from jumping) */}
      <div className="hidden md:block w-56 border-r border-border/50 bg-background" />
      {/* Main content skeleton */}
      <div className="flex-1 p-6">
        <div className="space-y-4 animate-pulse">
          <div className="h-6 w-48 rounded bg-muted" />
          <div className="h-4 w-80 rounded bg-muted/80" />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
            <div className="h-40 rounded-lg bg-muted" />
            <div className="h-40 rounded-lg bg-muted" />
            <div className="h-40 rounded-lg bg-muted" />
          </div>
        </div>
      </div>
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Suspense wraps just the route content; fallback shows skeleton */}
        <Suspense fallback={<RouteSkeleton />}>
          <Routes>
            <Route
              path="/"
              element={
                <RequireAuth>
                  <DashboardLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Home />} />
              <Route path="customize" element={<Customize />} />
            <Route path="assistant-mode" element={<AssistantMode />} />
              <Route path="assistant" element={<Assistant />} />
              {/* <Route path="notes" element={<Notes />} /> */} {/* Temporarily disabled */}
              <Route path="settings" element={<Settings />} />
              <Route path="pricing" element={<Pricing />} />
            </Route>

            <Route path="/auth" element={<Auth />} />
            <Route
              path="/user-details"
              element={
                <RequireAuth>
                  <UserDetails />
                </RequireAuth>
              }
            />
            <Route
              path="/interview"
              element={
                <RequireAuth>
                  <Interview />
                </RequireAuth>
              }
            />
            <Route
              path="/assistant/chat"
              element={
                <RequireAuth>
                  <AssistantChat />
                </RequireAuth>
              }
            />

            {/* Helpful redirects for common routes */}
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/signup" element={<Navigate to="/auth" replace />} />
            <Route path="/register" element={<Navigate to="/auth" replace />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;